import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Package, ChevronLeft, Upload, X, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleAddProduct = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerAuth()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [categories, setCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    stock: '',
    unit: 'bag',
    images: []
  })

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true)
        const response = await apiService.getKataleCategories()
        if (response.status === 'SUCCESS' && response.data) {
          setCategories(response.data)
        } else {
          // Fallback categories if API fails
          setCategories([
            { id: '1', name: 'Grains & Cereals' },
            { id: '2', name: 'Vegetables' },
            { id: '3', name: 'Fruits' },
            { id: '4', name: 'Legumes' },
            { id: '5', name: 'Dairy & Eggs' },
            { id: '6', name: 'Livestock' },
            { id: '7', name: 'Seeds & Seedlings' },
            { id: '8', name: 'Farm Equipment' },
            { id: '25', name: 'Others' },
          ])
        }
      } catch (err) {
        console.error('Error fetching categories:', err)
        // Fallback categories
        setCategories([
          { id: '1', name: 'Grains & Cereals' },
          { id: '2', name: 'Vegetables' },
          { id: '3', name: 'Fruits' },
          { id: '4', name: 'Legumes' },
          { id: '5', name: 'Dairy & Eggs' },
          { id: '6', name: 'Livestock' },
          { id: '7', name: 'Seeds & Seedlings' },
          { id: '8', name: 'Farm Equipment' },
          { id: '25', name: 'Others' },
        ])
      } finally {
        setCategoriesLoading(false)
      }
    }

    fetchCategories()
  }, [])

  const units = [
    { value: 'bag', label: 'Bag' },
    { value: 'kg', label: 'Kilogram (kg)' },
    { value: 'piece', label: 'Piece' },
    { value: 'crate', label: 'Crate' },
    { value: 'bunch', label: 'Bunch' },
    { value: 'tray', label: 'Tray' },
    { value: 'liter', label: 'Liter' },
    { value: 'dozen', label: 'Dozen' },
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    
    // Convert each file to base64 for persistent storage
    files.forEach((file, index) => {
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64Data = event.target.result // data:image/xxx;base64,...
        const newImage = {
          id: Date.now() + index,
          name: file.name,
          preview: base64Data, // For local preview
          data: base64Data,    // Base64 data to send to API
          file
        }
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, newImage].slice(0, 5) // Max 5 images
        }))
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    // Validation
    if (!formData.name.trim()) {
      setError('Product name is required')
      return
    }
    if (!formData.category) {
      setError('Please select a category')
      return
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setError('Please enter a valid price')
      return
    }
    if (!formData.stock || parseInt(formData.stock) < 0) {
      setError('Please enter available stock quantity')
      return
    }

    setLoading(true)

    // Helper to extract MG code from tenant DB (e.g., kololo_mg from mgfina6_tenant_kololo_mg)
    const getMgCode = (tenantDb) => {
      if (!tenantDb) return null
      const prefix = 'mgfina6_tenant_'
      if (tenantDb.startsWith(prefix)) {
        return tenantDb.substring(prefix.length)
      }
      const parts = tenantDb.split('_')
      return parts.length > 1 ? parts[1] : tenantDb
    }

    try {
      // Prepare product data for API with correct field names
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category_id: formData.category,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock),
        unit: formData.unit,
        // Seller info from customer context
        seller_mem_no: customer?.memberNo,
        seller_mg_code: getMgCode(customer?.tenantDb),
        seller_tenant_db: customer?.tenantDb,
        // For images, send base64 data for server-side storage
        images: formData.images.map(img => ({
          name: img.name,
          data: img.data  // base64 data URI - API will save to disk
        }))
      }

      const response = await apiService.addKataleProduct(productData)
      
      if (response.status === 'SUCCESS') {
        setSuccess(true)
        setTimeout(() => {
          navigate('/katale/my-products')
        }, 2000)
      } else {
        setError(response.message || 'Failed to add product. Please try again.')
      }
    } catch (err) {
      console.error('Error adding product:', err)
      setError(err.message || 'Failed to add product. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Added!</h2>
          <p className="text-gray-600 mb-4">Your product has been listed on MG Katale marketplace.</p>
          <p className="text-sm text-gray-500">Redirecting to your products...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Link to="/katale/my-products" className="text-gray-600 hover:text-gray-900 mr-4">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <Package className="h-6 w-6 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Add New Product</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Product Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Fresh Maize (50kg)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your product (quality, origin, etc.)"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  disabled={categoriesLoading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">{categoriesLoading ? 'Loading categories...' : 'Select a category'}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pricing & Stock */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Stock</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (UGX) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Stock *
                </label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <select
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {units.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Product Images */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Images</h2>
            <p className="text-sm text-gray-500 mb-4">Add up to 5 photos of your product. Good photos help sell faster!</p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {formData.images.map((image) => (
                <div key={image.id} className="relative">
                  <img
                    src={image.preview || image.url}
                    alt={image.name}
                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {formData.images.length < 5 && (
                <label className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50">
                  <Upload className="h-6 w-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Seller Info Preview */}
          <div className="bg-gray-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Seller Information</h2>
            <p className="text-sm text-gray-600 mb-2">Your product will be listed with the following seller details:</p>
            <div className="bg-white rounded-lg p-4">
              <p className="font-medium text-gray-900">{customer?.fullName || customer?.username || 'Your Name'}</p>
              <p className="text-sm text-gray-500">{customer?.mgCode || 'Your MG'}</p>
              <p className="text-sm text-gray-500">{customer?.phoneNumber || 'Your Phone'}</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4">
            <Link to="/katale/my-products" className="btn btn-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Adding Product...
                </>
              ) : (
                <>
                  <Package className="h-5 w-5 mr-2" />
                  Add Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default KataleAddProduct
